import React, { useState } from 'react';
import { Copy, CheckCircle, ChevronDown, ChevronRight, ExternalLink, Code, Lock, Globe } from 'lucide-react';
import { toast } from 'sonner';

const ApiDocs = () => {
  const [copiedCode, setCopiedCode] = useState(null);
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.tudominio.com';

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    toast.success('Código copiado');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const endpoints = [
    {
      category: 'Leads',
      description: 'Gestión de leads y prospectos',
      items: [
        {
          method: 'GET',
          path: '/api/leads',
          description: 'Obtiene lista de leads paginada',
          params: [
            { name: 'page', type: 'integer', default: '1', description: 'Número de página' },
            { name: 'limit', type: 'integer', default: '50', description: 'Leads por página' },
            { name: 'q', type: 'string', optional: true, description: 'Búsqueda por nombre, email, empresa' },
            { name: 'pipeline_id', type: 'string', optional: true, description: 'Filtrar por embudo' },
          ],
          response: `{
  "leads": [
    {
      "id": "lead-abc123",
      "name": "Juan Pérez",
      "email": "juan@empresa.com",
      "phone": "+57 300 123 4567",
      "company": "Empresa SAS",
      "documentType": "NIT",
      "documentNumber": "900123456-1",
      "personType": "Persona Jurídica",
      "value": 50000,
      "pipelineId": "pipeline-ventas",
      "stageId": "stage-nuevo",
      "priority": "Alta",
      "createdAt": "2024-01-15T10:30:00Z"
    }
  ],
  "total": 150,
  "page": 1,
  "pages": 3
}`,
          example: `curl -X GET "${BACKEND_URL}/api/leads?page=1&limit=10" \\
  -H "Content-Type: application/json"`,
        },
        {
          method: 'GET',
          path: '/api/leads/{lead_id}',
          description: 'Obtiene un lead específico por ID',
          params: [
            { name: 'lead_id', type: 'string', description: 'ID del lead' },
          ],
          response: `{
  "lead": {
    "id": "lead-abc123",
    "name": "Juan Pérez",
    "email": "juan@empresa.com",
    ...
  }
}`,
          example: `curl -X GET "${BACKEND_URL}/api/leads/lead-abc123"`,
        },
        {
          method: 'POST',
          path: '/api/leads',
          description: 'Crea un nuevo lead',
          body: `{
  "name": "María García",
  "email": "maria@empresa.com",
  "phone": "+57 310 987 6543",
  "company": "Tech Corp",
  "documentType": "CC",
  "documentNumber": "1234567890",
  "personType": "Persona Natural",
  "value": 25000,
  "pipelineId": "pipeline-ventas",
  "stageId": "stage-nuevo",
  "priority": "Media"
}`,
          response: `{
  "success": true,
  "lead": {
    "id": "lead-xyz789",
    ...
  }
}`,
          example: `curl -X POST "${BACKEND_URL}/api/leads" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "María García",
    "email": "maria@empresa.com",
    "pipelineId": "pipeline-ventas",
    "stageId": "stage-nuevo"
  }'`,
        },
        {
          method: 'PUT',
          path: '/api/leads/{lead_id}',
          description: 'Actualiza un lead existente',
          params: [
            { name: 'lead_id', type: 'string', description: 'ID del lead' },
          ],
          body: `{
  "name": "María García Actualizado",
  "value": 30000,
  "stageId": "stage-negociacion"
}`,
          response: `{
  "success": true,
  "lead": { ... }
}`,
          example: `curl -X PUT "${BACKEND_URL}/api/leads/lead-xyz789" \\
  -H "Content-Type: application/json" \\
  -d '{"value": 30000}'`,
        },
        {
          method: 'DELETE',
          path: '/api/leads/{lead_id}',
          description: 'Elimina un lead',
          params: [
            { name: 'lead_id', type: 'string', description: 'ID del lead' },
          ],
          response: `{
  "success": true,
  "message": "Lead eliminado"
}`,
          example: `curl -X DELETE "${BACKEND_URL}/api/leads/lead-xyz789"`,
        },
      ],
    },
    {
      category: 'Pipelines',
      description: 'Gestión de embudos de venta',
      items: [
        {
          method: 'GET',
          path: '/api/pipelines',
          description: 'Lista todos los embudos',
          response: `{
  "pipelines": [
    {
      "id": "pipeline-ventas",
      "name": "Ventas",
      "description": "Embudo principal de ventas",
      "stages": [
        { "id": "stage-nuevo", "name": "Nuevo Lead", "color": "#3b82f6", "order": 0 },
        { "id": "stage-contactado", "name": "Contactado", "color": "#f59e0b", "order": 1 }
      ]
    }
  ]
}`,
          example: `curl -X GET "${BACKEND_URL}/api/pipelines"`,
        },
        {
          method: 'POST',
          path: '/api/pipelines',
          description: 'Crea un nuevo embudo',
          body: `{
  "name": "Soporte",
  "description": "Embudo de tickets de soporte",
  "stages": [
    { "name": "Nuevo", "color": "#ef4444", "order": 0 },
    { "name": "En Progreso", "color": "#f59e0b", "order": 1 },
    { "name": "Resuelto", "color": "#22c55e", "order": 2 }
  ]
}`,
          response: `{
  "success": true,
  "pipeline": { ... }
}`,
          example: `curl -X POST "${BACKEND_URL}/api/pipelines" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Soporte", "stages": [...]}'`,
        },
      ],
    },
    {
      category: 'Actividades',
      description: 'Registro de actividades en leads',
      items: [
        {
          method: 'GET',
          path: '/api/activities',
          description: 'Lista actividades de un lead',
          params: [
            { name: 'leadId', type: 'string', description: 'ID del lead' },
          ],
          response: `{
  "activities": [
    {
      "id": "act-123",
      "leadId": "lead-abc",
      "type": "call",
      "description": "Llamada de seguimiento",
      "user": "Usuario",
      "createdAt": "2024-01-15T10:30:00Z"
    }
  ]
}`,
          example: `curl -X GET "${BACKEND_URL}/api/activities?leadId=lead-abc123"`,
        },
        {
          method: 'POST',
          path: '/api/activities',
          description: 'Crea una nueva actividad',
          body: `{
  "leadId": "lead-abc123",
  "type": "email",
  "description": "Envío de propuesta comercial"
}`,
          response: `{
  "success": true,
  "activity": { ... }
}`,
          example: `curl -X POST "${BACKEND_URL}/api/activities" \\
  -H "Content-Type: application/json" \\
  -d '{"leadId": "lead-abc", "type": "call", "description": "Llamada"}'`,
        },
      ],
    },
    {
      category: 'Form Capture',
      description: 'Captura de leads desde formularios externos',
      items: [
        {
          method: 'POST',
          path: '/api/form-capture',
          description: 'Captura un lead desde un formulario externo',
          body: `{
  "form_id": "form-contacto",
  "pipeline_id": "pipeline-ventas",
  "data": {
    "name": "Nuevo Contacto",
    "email": "contacto@email.com",
    "phone": "+57 300 000 0000",
    "company": "Empresa",
    "documentType": "NIT",
    "documentNumber": "900000000-0",
    "personType": "Persona Jurídica"
  }
}`,
          response: `{
  "success": true,
  "lead_id": "lead-nuevo123",
  "message": "Lead capturado exitosamente"
}`,
          example: `curl -X POST "${BACKEND_URL}/api/form-capture" \\
  -H "Content-Type: application/json" \\
  -d '{
    "form_id": "form-web",
    "pipeline_id": "pipeline-ventas",
    "data": { "name": "Juan", "email": "juan@mail.com" }
  }'`,
        },
      ],
    },
    {
      category: 'Estadísticas',
      description: 'Métricas y estadísticas del CRM',
      items: [
        {
          method: 'GET',
          path: '/api/stats',
          description: 'Obtiene estadísticas generales del dashboard',
          response: `{
  "totalLeads": 15000,
  "totalValue": 544000000,
  "leadsByPipeline": [
    { "pipelineId": "ventas", "count": 5000, "value": 180000000 }
  ],
  "leadsByStage": [
    { "stageId": "nuevo", "count": 1500 }
  ],
  "recentLeads": [ ... ]
}`,
          example: `curl -X GET "${BACKEND_URL}/api/stats"`,
        },
      ],
    },
  ];

  const methodColors = {
    GET: 'bg-green-100 text-green-700',
    POST: 'bg-blue-100 text-blue-700',
    PUT: 'bg-yellow-100 text-yellow-700',
    DELETE: 'bg-red-100 text-red-700',
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Documentación de la API</h1>
        <p className="text-slate-500 mt-2">Guía completa para integrar tu sistema con DataCRM</p>
      </div>

      {/* Quick Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Globe className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Base URL</p>
              <p className="font-mono text-sm text-slate-900">{BACKEND_URL}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Code className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Formato</p>
              <p className="font-mono text-sm text-slate-900">JSON</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Lock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Autenticación</p>
              <p className="font-mono text-sm text-slate-900">Bearer Token</p>
            </div>
          </div>
        </div>
      </div>

      {/* Authentication Section */}
      <section className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
        <h2 className="text-xl font-bold text-slate-900 mb-4">🔐 Autenticación</h2>
        <p className="text-slate-600 mb-4">
          Todas las peticiones a la API deben incluir un token de autenticación en el header:
        </p>
        <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm text-green-400">
          <code>Authorization: Bearer {'<tu_api_token>'}</code>
        </div>
        <p className="text-sm text-slate-500 mt-3">
          Obtén tu API Token en Configuración → API Access
        </p>
      </section>

      {/* Rate Limits */}
      <section className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
        <h2 className="text-lg font-bold text-amber-900 mb-2">⚠️ Límites de la API</h2>
        <ul className="text-sm text-amber-800 space-y-1">
          <li>• <strong>1,000 solicitudes</strong> por hora</li>
          <li>• <strong>10,000 solicitudes</strong> por día</li>
          <li>• Máximo <strong>100 registros</strong> por página en listados</li>
        </ul>
      </section>

      {/* Endpoints */}
      <div className="space-y-6">
        {endpoints.map((category) => (
          <section key={category.category} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">{category.category}</h2>
              <p className="text-sm text-slate-500">{category.description}</p>
            </div>
            
            <div className="divide-y divide-slate-100">
              {category.items.map((endpoint, idx) => {
                const endpointId = `${category.category}-${idx}`;
                const isExpanded = expandedEndpoint === endpointId;
                
                return (
                  <div key={idx} className="group">
                    <button
                      onClick={() => setExpandedEndpoint(isExpanded ? null : endpointId)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${methodColors[endpoint.method]}`}>
                          {endpoint.method}
                        </span>
                        <span className="font-mono text-sm text-slate-700">{endpoint.path}</span>
                        <span className="text-sm text-slate-500">{endpoint.description}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="px-6 pb-6 pt-2 bg-slate-50 space-y-4">
                        {/* Parameters */}
                        {endpoint.params && endpoint.params.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">Parámetros</h4>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-slate-500">
                                  <th className="pb-2">Nombre</th>
                                  <th className="pb-2">Tipo</th>
                                  <th className="pb-2">Descripción</th>
                                </tr>
                              </thead>
                              <tbody className="text-slate-700">
                                {endpoint.params.map((param, pIdx) => (
                                  <tr key={pIdx}>
                                    <td className="py-1 font-mono text-indigo-600">{param.name}</td>
                                    <td className="py-1">{param.type}</td>
                                    <td className="py-1">
                                      {param.description}
                                      {param.optional && <span className="text-slate-400 ml-2">(opcional)</span>}
                                      {param.default && <span className="text-slate-400 ml-2">default: {param.default}</span>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        
                        {/* Request Body */}
                        {endpoint.body && (
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">Cuerpo de la Petición</h4>
                            <div className="relative">
                              <pre className="bg-slate-900 rounded-lg p-4 text-green-400 text-xs overflow-x-auto">
                                {endpoint.body}
                              </pre>
                              <button
                                onClick={() => copyToClipboard(endpoint.body, `body-${endpointId}`)}
                                className="absolute top-2 right-2 p-1 bg-slate-700 rounded hover:bg-slate-600"
                              >
                                {copiedCode === `body-${endpointId}` ? (
                                  <CheckCircle className="w-4 h-4 text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4 text-slate-400" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* Response */}
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Respuesta</h4>
                          <div className="relative">
                            <pre className="bg-slate-900 rounded-lg p-4 text-green-400 text-xs overflow-x-auto">
                              {endpoint.response}
                            </pre>
                            <button
                              onClick={() => copyToClipboard(endpoint.response, `response-${endpointId}`)}
                              className="absolute top-2 right-2 p-1 bg-slate-700 rounded hover:bg-slate-600"
                            >
                              {copiedCode === `response-${endpointId}` ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <Copy className="w-4 h-4 text-slate-400" />
                              )}
                            </button>
                          </div>
                        </div>
                        
                        {/* Example */}
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Ejemplo cURL</h4>
                          <div className="relative">
                            <pre className="bg-slate-900 rounded-lg p-4 text-blue-400 text-xs overflow-x-auto">
                              {endpoint.example}
                            </pre>
                            <button
                              onClick={() => copyToClipboard(endpoint.example, `example-${endpointId}`)}
                              className="absolute top-2 right-2 p-1 bg-slate-700 rounded hover:bg-slate-600"
                            >
                              {copiedCode === `example-${endpointId}` ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <Copy className="w-4 h-4 text-slate-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* Error Codes */}
      <section className="bg-white rounded-xl border border-slate-200 p-6 mt-8">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Códigos de Error</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="pb-3">Código</th>
              <th className="pb-3">Descripción</th>
            </tr>
          </thead>
          <tbody className="text-slate-700">
            <tr className="border-b">
              <td className="py-3 font-mono text-green-600">200</td>
              <td>Éxito</td>
            </tr>
            <tr className="border-b">
              <td className="py-3 font-mono text-yellow-600">400</td>
              <td>Petición inválida - revisa los parámetros</td>
            </tr>
            <tr className="border-b">
              <td className="py-3 font-mono text-orange-600">401</td>
              <td>No autorizado - token inválido o expirado</td>
            </tr>
            <tr className="border-b">
              <td className="py-3 font-mono text-red-600">404</td>
              <td>Recurso no encontrado</td>
            </tr>
            <tr className="border-b">
              <td className="py-3 font-mono text-red-600">429</td>
              <td>Demasiadas peticiones - límite excedido</td>
            </tr>
            <tr>
              <td className="py-3 font-mono text-red-600">500</td>
              <td>Error interno del servidor</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default ApiDocs;
